import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2, RotateCcw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const LIMIT = 30;
const btn = "rounded-none uppercase tracking-wider text-xs";

// Record the founder note in the browser: 30s countdown, listen back, then save (captions auto-generated).
export default function FounderVoiceRecorder({ onSaved }) {
  const [state, setState] = useState("idle");
  const [left, setLeft] = useState(LIMIT);
  const [blob, setBlob] = useState(null);
  const rec = useRef(null);
  const timer = useRef(null);
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const stop = () => {
    clearInterval(timer.current);
    if (rec.current?.state === "recording") rec.current.stop();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setBlob(new Blob(chunks, { type: mr.mimeType || "audio/webm" }));
        setState("review");
      };
      rec.current = mr;
      mr.start();
      setLeft(LIMIT);
      setState("recording");
      timer.current = setInterval(() => setLeft((s) => {
        if (s <= 1) { stop(); return 0; }
        return s - 1;
      }), 1000);
    } catch {
      toast.error("Microphone blocked. Allow mic access in your browser, or upload a file instead.");
    }
  };

  const save = async () => {
    setState("saving");
    const form = new FormData();
    const type = (blob.type || "audio/webm").split(";")[0];
    form.append("audio", new File([blob], `founder-note.${type.split("/")[1] || "webm"}`, { type }));
    try {
      const { data } = await api.post("/admin/founder-voice/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved(data);
      setBlob(null);
      setState("idle");
      toast.success("Your voice note is live on /about. Captions were generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the recording.");
      setState("review");
    }
  };

  return (
    <div className="mb-8 border border-obsidian-600 p-4" data-testid="founder-voice-recorder">
      <p className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta mb-3">Record now · up to {LIMIT} seconds</p>
      {state === "recording" ? (
        <div className="flex items-center gap-4">
          <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
          <span className="font-serif text-3xl text-bone-100 tabular-nums" data-testid="founder-voice-countdown">0:{String(left).padStart(2, "0")}</span>
          <Button onClick={stop} data-testid="founder-voice-record-stop" className={`${btn} bg-transparent border border-bone-500 text-bone-100 hover:border-terracotta hover:bg-transparent`}>
            <Square className="h-4 w-4 mr-2" /> Stop
          </Button>
        </div>
      ) : blob ? (
        <div className="flex items-center gap-3 flex-wrap">
          <audio controls src={url} data-testid="founder-voice-record-playback" className="max-w-full" />
          <Button onClick={save} disabled={state === "saving"} data-testid="founder-voice-record-save" className={`${btn} bg-terracotta hover:bg-terracotta-deep text-obsidian-900`}>
            {state === "saving" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />} Use this take
          </Button>
          <Button onClick={() => { setBlob(null); setState("idle"); }} disabled={state === "saving"} data-testid="founder-voice-record-retake" className={`${btn} bg-transparent border border-obsidian-600 text-bone-400 hover:text-terracotta hover:bg-transparent`}>
            <RotateCcw className="h-4 w-4 mr-2" /> Retake
          </Button>
        </div>
      ) : (
        <Button onClick={start} data-testid="founder-voice-record-start" className={`${btn} bg-transparent border border-bone-500 text-bone-100 hover:border-terracotta hover:text-terracotta hover:bg-transparent`}>
          <Mic className="h-4 w-4 mr-2" /> Record now
        </Button>
      )}
    </div>
  );
}
